import React, { ReactNode } from 'react';
import { flow } from 'lodash';
import hexToRgba from 'hex-to-rgba';
import Floater, { Placement } from 'react-floater';
import styled from '@emotion/styled';

import colours from '~/utils/colours';
import Styleable, {
  createStyledComponent,
  StyleableProps,
  GetStyleFn,
} from '~/App/Components/HOC/Styleable';
import ReplaceableComponents from '~/components/higher-order/ReplaceableComponents';

export const themes = {
  helper: {
    foreground: 'white',
    background: colours.blue,
    opacity: 1,
  },

  dark: {
    foreground: 'white',
    background: colours.textFormControl,
    opacity: 1,
  },

  black: {
    foreground: 'white',
    background: '#000',
    opacity: 1,
  },
};

interface ThemeProps {
  foreground: string;
  background: string;
  opacity?: number;
}

interface TooltipStyleProps extends StyleableProps {
  props?: {
    classNames?: string;
    theme: ThemeProps;
  } & Record<string, React.CSSProperties>;
}

export const styles = {
  body: ({ props }: TooltipStyleProps) => {
    return {
      opacity: props?.styles.opacity || props?.theme.opacity || 1,
      color: props?.theme.foreground,
      backgroundColor: props?.theme?.background,

      padding: '5px',
      fontWeight: 'bold',
      borderRadius: '5px',
    } as React.CSSProperties;
  },

  header: (props: Record<string, React.CSSProperties>) =>
    ({
      ...props,
      fontSize: '1.75em',
      fontWeight: 'bold',
    } as React.CSSProperties),

  arrow: ({ props }: TooltipStyleProps) => {
    const opacity = (props?.opacity || props?.theme?.opacity || 1) as number;
    const color = hexToRgba(
      props?.theme?.background || themes.black.background,
      opacity
    );

    return {
      ...props,
      color,
      length: 5,
      spread: 10,
    } as React.CSSProperties;
  },
};

export const defaultComponents = {
  HeaderContainer: createStyledComponent('div', 'header'),
  BodyContainer: createStyledComponent('div', 'body'),
};

type ComponentsProps = typeof defaultComponents;

export interface TooltipWrapperProps {
  id: string;
  show: boolean;
  placement?: Placement;
  header?: ReactNode;
  body: ReactNode;
  children: ReactNode;
  opacity?: number;
  components?: ComponentsProps;
  styles: React.CSSProperties;
  event: string;
  getStyles: GetStyleFn;
  theme: ThemeProps;
  className?: string;
  cursor?: string;
  disabled?: boolean;
  arrowStyles?: React.CSSProperties;
}

function TooltipWrapper(props: TooltipWrapperProps) {
  const {
    id,
    header,
    body,
    components = defaultComponents,
    placement,
    children,
    show,
    event = 'hover',
    getStyles,
    theme = themes.dark,
    className,
    opacity,
    cursor,
    disabled = false,
    arrowStyles,
  } = props;

  const { BodyContainer, HeaderContainer } = components;

  const ThemedBodyContainer = styled(BodyContainer)`
    opacity: ${theme.opacity ?? 1};
    color: ${theme.foreground};
    background-color: ${theme.background};
  `;

  function Wrapper() {
    return (
      <ThemedBodyContainer
        getStyles={getStyles}
        className={className}
        styles={{ opacity }}
      >
        {!header || (
          <HeaderContainer getStyles={getStyles}>{header}</HeaderContainer>
        )}
        {body}
      </ThemedBodyContainer>
    );
  }

  const color = hexToRgba(theme.background, theme.opacity ?? 1);

  return disabled ? (
    children
  ) : (
    <Floater
      id={id}
      open={show}
      offset={0}
      eventDelay={0}
      event={event}
      component={Wrapper}
      placement={placement}
      styles={{
        arrow: { ...getStyles('arrow', styles), color },
        wrapper: { cursor: cursor ?? 'help', ...arrowStyles },
      }}
      content={undefined}
    >
      {children}
    </Floater>
  );
}

const Tooltip = flow<
  unknown[],
  unknown,
  React.FC<Partial<TooltipWrapperProps>>
>(
  Styleable(styles),
  ReplaceableComponents(defaultComponents)
)(TooltipWrapper);

export default Tooltip;
